import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Translate } from 'react-redux-i18n';
import TradingViewChart from 'components/TradingViewChart';
import MiniLoader from 'components/MiniLoader';
import useStockCandles from 'hooks/useStockCandles';
import { TIMEFRAMES } from 'utils/mockStockCandles';
import * as stockActions from 'redux/actions/stockActions';
import * as quoteActions from 'redux/actions/quoteActions';
import 'assets/css/stock.css';

function formatPrice(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) {
        return '—';
    }
    return n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function resolvePrice(item, liveQuote) {
    const live = Number(liveQuote?.c || liveQuote?.pc);
    if (live > 0) return live;
    const ask = Number(item?.price ?? item?.ask);
    return ask > 0 ? ask : 0;
}

const StockChart = (props) => {
    const ticker = String(props.match.params.ticker || '').trim().toUpperCase();
    const [timeframe, setTimeframe] = useState('1d');

    useEffect(() => {
        if (!ticker || !props.user_id) {
            return undefined;
        }
        props.loadItem({ id: props.user_id, symbol: ticker }, props.token);
        props.loadStockQuotes([ticker], props.user_id, props.token);
        const interval = setInterval(() => {
            props.loadStockQuotes([ticker], props.user_id, props.token);
        }, 60000);
        return () => clearInterval(interval);
    }, [ticker, props.user_id]);

    const { candles, loading, error } = useStockCandles(
        ticker,
        timeframe,
        props.user_id,
        Boolean(ticker) && Boolean(props.user_id),
    );

    const item = props.item?.symbol?.toUpperCase() === ticker ? props.item : null;
    const liveQuote = props.quotes?.[ticker];
    const price = resolvePrice(item, liveQuote);
    const name = item?.name || '';
    const profileUrl = `/stock/${encodeURIComponent(ticker)}`;

    if (props.loadingItem && !item) {
        return (
            <div className="stock-chart-page stock-loading">
                <MiniLoader />
            </div>
        );
    }

    return (
        <div className="stock-chart-page">
            <header className="stock-chart-page-bar">
                <div className="stock-chart-page-identity">
                    <Link
                        to={profileUrl}
                        className="btn btn-secondary btn-sm stock-chart-back-btn"
                    >
                        <Translate value="stock_chart_back" />
                    </Link>
                    <div className="stock-chart-page-title-block">
                        <h1 className="stock-chart-page-title">
                            {ticker}
                            {name ? (
                                <span className="stock-chart-page-name">
                                    {' · '}
                                    {name}
                                </span>
                            ) : null}
                        </h1>
                        <p className="stock-chart-page-price">
                            {formatPrice(price)}
                            <span> USD</span>
                        </p>
                    </div>
                </div>
            </header>

            <div className="stock-chart-page-body">
                <TradingViewChart
                    symbol={ticker || 'STOCK'}
                    basePrice={price}
                    candles={candles}
                    loading={loading}
                    error={error}
                    timeframe={timeframe}
                    onTimeframeChange={setTimeframe}
                    timeframes={TIMEFRAMES}
                    height={560}
                    drawingsEnabled
                    userId={props.user_id}
                />
            </div>
        </div>
    );
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        token: reducer.session.auth.token,
        item: reducer.stock.item,
        loadingItem: reducer.stock.loading,
        quotes: reducer.event.stock,
    };
}

const mapDispatchToProps = (dispatch) => ({
    loadItem: (params, token) =>
        dispatch(stockActions.loadItem({ params, token })),
    loadStockQuotes: (symbols, user_id, token) =>
        dispatch(quoteActions.loadStockQuotes({ symbols, user_id, token })),
});

export default connect(mapStateToProps, mapDispatchToProps)(StockChart);
